// Page to handle scanned friend request QR codes
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { doc, getDoc, collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { sendFriendRequest } from '../lib/firestore/friends';
import '../styles/FriendRequestQR.css';

const FriendRequestQR = () => {
  const { userId } = useParams();
  const { user, loading: authLoading } = useAuthState();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [targetUser, setTargetUser] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        // Not logged in - redirect to login with return path
        navigate(`/login?redirect=/friend-request/${userId}`);
        return;
      }
      
      if (user.uid === userId) {
        // Can't send friend request to yourself
        setError("You can't send a friend request to yourself!");
        setLoading(false);
        return;
      }

      processFriendRequest();
    }
  }, [user, authLoading, userId, navigate]);

  const processFriendRequest = async () => {
    if (!user || !userId) return;

    setLoading(true);
    setError('');

    try {
      // Get target user info
      const targetUserDoc = await getDoc(doc(db, 'users', userId));
      if (!targetUserDoc.exists()) {
        setError('User not found');
        setLoading(false);
        return;
      }

      const targetUserData = targetUserDoc.data();
      setTargetUser({
        id: userId,
        ...targetUserData,
      });

      // Check if already friends
      const pairId = [user.uid, userId].sort().join('_');
      const friendshipRef = doc(db, 'friendships', pairId);
      const friendship = await getDoc(friendshipRef);
      
      if (friendship.exists()) {
        setError('You are already friends with this person!');
        setLoading(false);
        return;
      }

      // Check if friend request already exists
      const requestsRef = collection(db, 'friendRequests');
      const existingQ = query(
        requestsRef,
        where('fromUserId', '==', user.uid),
        where('toUserId', '==', userId),
        where('status', 'in', ['pending', 'accepted'])
      );
      const existing = await getDocs(existingQ);
      
      if (!existing.empty) {
        setError('Friend request already sent!');
        setLoading(false);
        return;
      }

      // Send friend request
      await sendFriendRequest(user.uid, userId);

      // Add target user to scanner's contacts (if not already there)
      try {
        const contactsRef = collection(db, 'users', user.uid, 'contacts');
        const targetPhone = targetUserData.phone || targetUserData.phoneNumber;
        
        if (targetPhone) {
          const phoneE164 = targetPhone.startsWith('+') ? targetPhone : `+${targetPhone}`;
          const existingContactQuery = query(
            contactsRef,
            where('phoneE164', '==', phoneE164)
          );
          const existingContact = await getDocs(existingContactQuery);
          
          if (existingContact.empty) {
            // Add to contacts
            await addDoc(contactsRef, {
              name: `${targetUserData.firstName || ''} ${targetUserData.lastName || ''}`.trim() || 'Friend',
              phoneE164: phoneE164,
              email: targetUserData.email || null,
              note: 'Added via QR code scan',
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            });
          }
        }
      } catch (contactError) {
        // Don't fail the whole process if contact add fails
        console.warn('Failed to add to contacts:', contactError);
      }

      // Also add scanner to target user's contacts (if not already there)
      try {
        const targetContactsRef = collection(db, 'users', userId, 'contacts');
        const scannerPhone = user.phoneNumber || (user.email ? null : null);
        
        if (scannerPhone) {
          const phoneE164 = scannerPhone.startsWith('+') ? scannerPhone : `+${scannerPhone}`;
          const existingContactQuery = query(
            targetContactsRef,
            where('phoneE164', '==', phoneE164)
          );
          const existingContact = await getDocs(existingContactQuery);
          
          if (existingContact.empty) {
            const scannerUserDoc = await getDoc(doc(db, 'users', user.uid));
            const scannerUserData = scannerUserDoc.exists() ? scannerUserDoc.data() : {};
            
            await addDoc(targetContactsRef, {
              name: `${scannerUserData.firstName || ''} ${scannerUserData.lastName || ''}`.trim() || 'Friend',
              phoneE164: phoneE164,
              email: scannerUserData.email || null,
              note: 'Added via QR code scan',
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            });
          }
        }
      } catch (contactError) {
        // Don't fail the whole process if contact add fails
        console.warn('Failed to add scanner to target contacts:', contactError);
      }

      setSuccess(true);
    } catch (err) {
      console.error('Error processing friend request:', err);
      setError(err.message || 'Failed to send friend request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="friend-request-qr-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Processing friend request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="friend-request-qr-container">
        <div className="error-state">
          <h2>❌ {error}</h2>
          <button className="btn btn-primary" onClick={() => navigate('/home')}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (success && targetUser) {
    const displayName = targetUser.firstName || targetUser.lastName
      ? `${targetUser.firstName || ''} ${targetUser.lastName || ''}`.trim()
      : 'Friend';

    return (
      <div className="friend-request-qr-container">
        <div className="success-state">
          <h2>✅ Friend Request Sent!</h2>
          <div className="user-info">
            <div className="user-avatar">
              {targetUser.profilePicture ? (
                <img src={targetUser.profilePicture} alt={displayName} />
              ) : (
                <span>{displayName.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <h3>{displayName}</h3>
            <p>You've sent a friend request to {displayName}.</p>
            <p className="info-text">They've also been added to your contacts!</p>
          </div>
          <div className="actions">
            <button className="btn btn-primary" onClick={() => navigate('/contacts')}>
              Go to Contacts
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/home')}>
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default FriendRequestQR;

